/**
 * Data layer for `quarterly-award-recognition`. Thin typed fetch wrappers over /api/v1/quarterly-award-recognition.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface QuarterlyAwardRecognitionRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListQuarterlyAwardRecognitionResponse {
	count: number;
	items: QuarterlyAwardRecognitionRow[];
}

export interface CreateQuarterlyAwardRecognitionBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/quarterly-award-recognition";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListQuarterlyAwardRecognitionResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListQuarterlyAwardRecognitionResponse>(res);
}

export async function create(body: CreateQuarterlyAwardRecognitionBody): Promise<QuarterlyAwardRecognitionRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<QuarterlyAwardRecognitionRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const quarterlyAwardRecognitionQueryKeys = {
	all: ["quarterly-award-recognition"] as const,
	list: () => [...quarterlyAwardRecognitionQueryKeys.all] as const,
	detail: (id: string) => [...quarterlyAwardRecognitionQueryKeys.all, id] as const,
};
